import { useRef, useState, type ReactNode } from 'react';
import {
  Brain,
  ChevronRight,
  Feather,
  FileStack,
  FileText,
  Plug,
  Plus,
  Puzzle,
  Sparkles,
  Telescope,
  Wrench,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { cn } from '@/shared/cn';
import {
  DISABLED_INTERACTIVE,
  MENU_CONTENT,
  MENU_ITEM_BASE,
  MENU_SECTION_LABEL,
  MENU_SUB_CONTENT,
} from '@/shared/ui-class';
import {
  TAIL_PRESERVE_MESSAGE_COUNT,
  type ComposerStyleId,
} from '@/versions/v1/shared/chat-types';
import { PROJECT_TILES } from '@/versions/v1/shared/project-seed';
import { useChat } from '@/versions/v1/shared/ChatContext';
import { useClickOutside } from '@/versions/v1/chrome/sidebar/AccountMenu';

type Submenu = 'projects' | 'skills' | 'connectors' | 'plugins' | 'thinking' | null;

const MENU_SEPARATOR = 'my-1 h-px bg-zinc-200 dark:bg-zinc-800';

const THINKING_MODES = [
  { id: 'off', label: 'Off', description: 'No extended reasoning budget.' },
  { id: 'adaptive', label: 'Adaptive', description: 'Scale thinking to task complexity.' },
  { id: 'thinking', label: 'Thinking', description: 'Always use the thinking tier.' },
  { id: 'extended', label: 'Extended', description: 'Longer internal reasoning window.' },
  { id: 'research', label: 'Research', description: 'Deep research with web tools.' },
] as const;

const STYLES: Array<{ id: ComposerStyleId; label: string; description: string }> = [
  { id: 'default', label: 'Default', description: 'Model picks its own register.' },
  { id: 'concise', label: 'Concise', description: 'Short, declarative, no preamble.' },
  { id: 'formal', label: 'Formal', description: 'Professional tone and structure.' },
];

export function ComposerPlusMenu() {
  const {
    messages,
    composerStyle,
    setComposerStyle,
    compactConversation,
  } = useChat();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submenu, setSubmenu] = useState<Submenu>(null);
  const [webSearch, setWebSearch] = useState(true);
  const [builtinTools, setBuiltinTools] = useState(true);
  const [thinkingMode, setThinkingMode] = useState<(typeof THINKING_MODES)[number]['id']>('adaptive');
  const ref = useRef<HTMLDivElement | null>(null);

  useClickOutside(ref, () => {
    setOpen(false);
    setSubmenu(null);
  }, open);

  const close = () => {
    setOpen(false);
    setSubmenu(null);
  };

  const canCompact = messages.length > TAIL_PRESERVE_MESSAGE_COUNT;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        title="Add tools"
        aria-label="Add tools"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-md text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 dark:text-zinc-400 dark:hover:bg-zinc-800/60 dark:hover:text-zinc-100 dark:focus-visible:ring-zinc-700',
          open && 'bg-zinc-100 dark:bg-zinc-800/60',
        )}
      >
        <Plus className="h-4 w-4" />
      </button>
      {open ? (
        <div
          className={cn(
            MENU_CONTENT,
            'absolute bottom-full left-0 z-50 mb-2 min-w-[240px] overflow-visible p-1',
          )}
        >
          <SubmenuRow
            label="Projects"
            icon={<FileStack className="h-4 w-4" />}
            active={submenu === 'projects'}
            onOpen={() => setSubmenu('projects')}
          />
          <SubmenuRow
            label="Skills"
            icon={<Sparkles className="h-4 w-4" />}
            active={submenu === 'skills'}
            onOpen={() => setSubmenu('skills')}
          />
          <SubmenuRow
            label="Connectors"
            icon={<Plug className="h-4 w-4" />}
            active={submenu === 'connectors'}
            onOpen={() => setSubmenu('connectors')}
          />
          <SubmenuRow
            label="Plugins"
            icon={<Puzzle className="h-4 w-4" />}
            active={submenu === 'plugins'}
            onOpen={() => setSubmenu('plugins')}
          />

          <div className={MENU_SEPARATOR} />

          <div className={MENU_SECTION_LABEL}>Thinking</div>
          {THINKING_MODES.map((mode) => (
            <button
              key={mode.id}
              type="button"
              className={cn(
                MENU_ITEM_BASE,
                'w-full flex-col items-start gap-0.5 py-2',
                thinkingMode === mode.id && 'bg-zinc-100 dark:bg-zinc-900',
              )}
              onClick={() => setThinkingMode(mode.id)}
            >
              <span className="inline-flex items-center gap-2 font-medium">
                <Brain className="h-4 w-4 text-zinc-500" />
                {mode.label}
              </span>
              <span className="pl-6 text-xs text-muted">{mode.description}</span>
            </button>
          ))}

          <div className={MENU_SEPARATOR} />

          <div className={MENU_SECTION_LABEL}>Tools</div>
          <ToggleRow
            title="Web search"
            description="Bias toward installed search connectors"
            icon={<Telescope className="h-4 w-4" />}
            checked={webSearch}
            onChange={setWebSearch}
          />
          <ToggleRow
            title="Built-in tools"
            description={
              builtinTools
                ? 'Built-in agent toolset (file operations, shell, tasks, …)'
                : 'Off — agent uses only connectors, skills, and plugins'
            }
            icon={<Wrench className="h-4 w-4" />}
            checked={builtinTools}
            onChange={setBuiltinTools}
          />

          {canCompact ? (
            <button
              type="button"
              className={cn(MENU_ITEM_BASE, 'w-full')}
              onClick={() => {
                compactConversation();
                close();
              }}
            >
              <FileText className="h-4 w-4 text-zinc-500" />
              Compact conversation
            </button>
          ) : null}

          <div className={MENU_SEPARATOR} />

          <div className={MENU_SECTION_LABEL}>Style</div>
          {STYLES.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={cn(
                MENU_ITEM_BASE,
                'w-full flex-col items-start gap-0.5 py-2',
                composerStyle === preset.id && 'bg-zinc-100 dark:bg-zinc-900',
              )}
              onClick={() => setComposerStyle(preset.id)}
            >
              <span className="inline-flex items-center gap-2 font-medium">
                <Feather className="h-4 w-4 text-zinc-500" />
                {preset.label}
              </span>
              <span className="pl-6 text-xs text-muted">{preset.description}</span>
            </button>
          ))}

          {submenu === 'projects' ? (
            <SubmenuPanel title="Projects">
              <button type="button" className={MENU_ITEM_BASE} onClick={close}>
                Recents
              </button>
              {PROJECT_TILES.map((project) => (
                <button
                  key={project.slug}
                  type="button"
                  className={MENU_ITEM_BASE}
                  onClick={() => {
                    close();
                    navigate(`/projects/${project.slug}/chat`);
                  }}
                >
                  {project.name}
                </button>
              ))}
            </SubmenuPanel>
          ) : null}
          {submenu === 'skills' ? (
            <SubmenuPanel title="Skills">
              <button type="button" className={MENU_ITEM_BASE} onClick={close}>
                status-summary
              </button>
              <Link to="/settings/skills" className={MENU_ITEM_BASE} onClick={close}>
                Manage skills
              </Link>
            </SubmenuPanel>
          ) : null}
          {submenu === 'connectors' ? (
            <SubmenuPanel title="Connectors">
              <ToggleRow title="GitHub" description="Repository access" checked onChange={() => undefined} />
              <ToggleRow
                title="Notion"
                description="Workspace pages"
                checked={false}
                onChange={() => undefined}
              />
              <Link to="/settings/connectors" className={MENU_ITEM_BASE} onClick={close}>
                Manage connectors
              </Link>
            </SubmenuPanel>
          ) : null}
          {submenu === 'plugins' ? (
            <SubmenuPanel title="Plugins">
              <ToggleRow title="Atlassian" description="Jira and Confluence" checked onChange={() => undefined} />
              <Link to="/settings/plugins" className={MENU_ITEM_BASE} onClick={close}>
                Manage plugins
              </Link>
            </SubmenuPanel>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function SubmenuRow({
  label,
  icon,
  active,
  onOpen,
}: {
  label: string;
  icon: ReactNode;
  active: boolean;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      className={cn(MENU_ITEM_BASE, 'w-full justify-between', active && 'bg-zinc-100 dark:bg-zinc-900')}
      onMouseEnter={onOpen}
      onFocus={onOpen}
      onClick={onOpen}
    >
      <span className="inline-flex items-center gap-2">
        {icon}
        {label}
      </span>
      <ChevronRight className="h-4 w-4 text-zinc-400" />
    </button>
  );
}

function SubmenuPanel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className={cn(MENU_SUB_CONTENT, 'absolute left-full top-0 ml-1 min-w-[200px] p-1')}>
      <div className={MENU_SECTION_LABEL}>{title}</div>
      {children}
    </div>
  );
}

function ToggleRow({
  title,
  description,
  icon,
  checked,
  onChange,
}: {
  title: string;
  description: string;
  icon?: React.ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      className={cn(
        MENU_ITEM_BASE,
        'w-full flex-col items-start gap-0.5 py-2',
        DISABLED_INTERACTIVE,
      )}
      onClick={() => onChange(!checked)}
    >
      <span className="inline-flex w-full items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 font-medium">
          {icon}
          {title}
        </span>
        <span
          className={cn(
            'h-5 w-9 shrink-0 rounded-full p-0.5 transition-colors',
            checked ? 'bg-accent-500' : 'bg-zinc-300 dark:bg-zinc-700',
          )}
        >
          <span
            className={cn(
              'block h-4 w-4 rounded-full bg-white transition-transform',
              checked ? 'translate-x-4' : 'translate-x-0',
            )}
          />
        </span>
      </span>
      <span className="text-xs text-muted">{description}</span>
    </button>
  );
}
